import { rgbAt } from '../image/analysis.js';
import type { NormalizedRect } from '../types/geometry.js';
import { clamp01, roundTo } from '../types/geometry.js';
import type { AnalysisBuffer } from '../types/image.js';
import type { FaceGeometryMetrics } from '../types/metrics.js';
import {
  clampRect,
  computeSkinReference,
  faceRegionRects,
  heuristicOptions,
  insetRect,
  isHairLike,
  isSkinLike,
  localDetailAt,
  pixelBounds,
  pixelFeatures,
  type HairHeuristicOptions,
} from './hairOcclusion.js';

/**
 * Foreign-object analysis for the «Лицо не закрыто посторонними предметами»
 * check.
 *
 * Hair has its own check, so this scan only counts large *connected* regions
 * inside the face ellipse that are neither skin nor hair, with sharp edges —
 * a mask, a band, a hand, a microphone, a phone. A strong left/right asymmetry
 * is accepted as a second, independent symptom (a hand on one side).
 *
 * Everything is measured against the actual face ellipse, not the bounding box:
 * the bright corners of a face box are background, not a white mask.
 */

export interface FaceObstructionOverrides {
  darkFactor?: number;
  saturationFactor?: number;
  detailFactor?: number;
}

export interface FaceObstructionAnalysisInput {
  pixels: AnalysisBuffer;
  geometry: FaceGeometryMetrics;
  overrides?: FaceObstructionOverrides;
}

export interface ObstructionComponent {
  area: number;
  rect: NormalizedRect;
  centroid: { x: number; y: number };
  /** Share of the component boundary with a strong luma edge, 0..1. */
  boundarySharpness: number;
}

export interface FaceObstructionAnalysis {
  /** Largest component area over scanned face pixels, 0..1. */
  coverage: number;
  largest: ObstructionComponent | undefined;
  componentCount: number;
  /** |left - right| share of foreign pixels, 0..1. */
  asymmetry: number;
  boundarySharpness: number;
  scannedPixels: number;
  scanRect: NormalizedRect;
  faceEllipse: { centerX: number; centerY: number; rx: number; ry: number };
  confidence: number;
  mode: 'heuristic' | 'segmentation';
  notes: string[];
}

interface ComponentStats {
  area: number;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  sumX: number;
  sumY: number;
  boundarySharp: number;
  boundaryTotal: number;
}

const analyseComponents = (
  pixels: AnalysisBuffer,
  bounds: { x0: number; y0: number; x1: number; y1: number },
  foreign: Uint8Array,
): ObstructionComponent[] => {
  const { luma, width, height } = pixels;
  const boundsWidth = Math.max(0, bounds.x1 - bounds.x0);
  const boundsHeight = Math.max(0, bounds.y1 - bounds.y0);
  const visited = new Uint8Array(foreign.length);
  const components: ObstructionComponent[] = [];

  const lumaAt = (x: number, y: number): number => luma[y * width + x] as number;

  for (let start = 0; start < foreign.length; start += 1) {
    if (foreign[start] !== 1 || visited[start] === 1) continue;
    visited[start] = 1;
    const stack: number[] = [start];
    const stats: ComponentStats = {
      area: 0,
      minX: boundsWidth,
      minY: boundsHeight,
      maxX: -1,
      maxY: -1,
      sumX: 0,
      sumY: 0,
      boundarySharp: 0,
      boundaryTotal: 0,
    };

    while (stack.length > 0) {
      const current = stack.pop() as number;
      const localX = current % boundsWidth;
      const localY = Math.floor(current / boundsWidth);
      const x = bounds.x0 + localX;
      const y = bounds.y0 + localY;
      stats.area += 1;
      stats.sumX += x;
      stats.sumY += y;
      if (localX < stats.minX) stats.minX = localX;
      if (localY < stats.minY) stats.minY = localY;
      if (localX > stats.maxX) stats.maxX = localX;
      if (localY > stats.maxY) stats.maxY = localY;

      const neighbours = [
        { index: localX > 0 ? current - 1 : -1, x: x - 1, y },
        { index: localX < boundsWidth - 1 ? current + 1 : -1, x: x + 1, y },
        { index: localY > 0 ? current - boundsWidth : -1, x, y: y - 1 },
        { index: localY < boundsHeight - 1 ? current + boundsWidth : -1, x, y: y + 1 },
      ] as const;

      let boundary = false;
      let sharp = false;
      const centerLuma = lumaAt(x, y);
      for (const neighbour of neighbours) {
        if (neighbour.index < 0 || foreign[neighbour.index] !== 1) {
          boundary = true;
          if (
            neighbour.x >= 0 &&
            neighbour.y >= 0 &&
            neighbour.x < width &&
            neighbour.y < height &&
            Math.abs(lumaAt(neighbour.x, neighbour.y) - centerLuma) > 18
          ) {
            sharp = true;
          }
          continue;
        }
        if (visited[neighbour.index] !== 1) {
          visited[neighbour.index] = 1;
          stack.push(neighbour.index);
        }
      }
      if (boundary) {
        stats.boundaryTotal += 1;
        if (sharp) stats.boundarySharp += 1;
      }
    }

    if (stats.area > 0 && stats.maxX >= stats.minX) {
      const x = (bounds.x0 + stats.minX) / width;
      const y = (bounds.y0 + stats.minY) / height;
      components.push({
        area: stats.area,
        rect: {
          x: roundTo(clamp01(x), 4),
          y: roundTo(clamp01(y), 4),
          width: roundTo(clamp01((stats.maxX - stats.minX + 1) / width), 4),
          height: roundTo(clamp01((stats.maxY - stats.minY + 1) / height), 4),
        },
        centroid: {
          x: roundTo((bounds.x0 + stats.sumX / stats.area) / width, 4),
          y: roundTo((bounds.y0 + stats.sumY / stats.area) / height, 4),
        },
        boundarySharpness:
          stats.boundaryTotal > 0 ? roundTo(stats.boundarySharp / stats.boundaryTotal, 3) : 0,
      });
    }
  }

  components.sort((a, b) => b.area - a.area);
  return components;
};

export const analyseFaceObstruction = (
  input: FaceObstructionAnalysisInput,
): FaceObstructionAnalysis => {
  const { pixels, geometry } = input;
  const { data, width, height, channels, luma } = pixels;
  const notes: string[] = [];

  const scanRect = clampRect({
    x: geometry.faceBox.x,
    y: geometry.faceBox.y,
    width: geometry.faceBox.width,
    height: geometry.faceBox.height,
  });
  const faceEllipse = {
    centerX: geometry.faceCenterX,
    centerY: (geometry.foreheadPoint.y + geometry.chinPoint.y) / 2,
    rx: Math.max(geometry.headWidth * 0.42, geometry.faceBox.width * 0.3),
    ry: Math.max(geometry.faceHeight * 0.46, geometry.faceBox.height * 0.3),
  };

  const skinReference = computeSkinReference(pixels, insetRect(geometry.faceBox, 0.18));
  const options: HairHeuristicOptions = heuristicOptions(skinReference, input.overrides);

  const exclusionZones = faceRegionRects(geometry)
    .filter((region) => region.id !== 'cheeks' && region.id !== 'nose')
    .map((region) => {
      const rect = region.rect;
      return {
        x: rect.x - rect.width * 0.1,
        y: rect.y - rect.height * 0.1,
        width: rect.width * 1.2,
        height: rect.height * 1.2,
      };
    });

  const bounds = pixelBounds(scanRect, width, height);
  const boundsWidth = Math.max(0, bounds.x1 - bounds.x0);
  const boundsHeight = Math.max(0, bounds.y1 - bounds.y0);
  const inside = new Uint8Array(boundsWidth * boundsHeight);
  const foreign = new Uint8Array(boundsWidth * boundsHeight);

  let leftTotal = 0;
  let leftForeign = 0;
  let rightTotal = 0;
  let rightForeign = 0;
  let scannedPixels = 0;

  for (let y = bounds.y0; y < bounds.y1; y += 1) {
    for (let x = bounds.x0; x < bounds.x1; x += 1) {
      const nx = (x + 0.5) / width;
      const ny = (y + 0.5) / height;
      const dx = (nx - faceEllipse.centerX) / Math.max(faceEllipse.rx, 1e-6);
      const dy = (ny - faceEllipse.centerY) / Math.max(faceEllipse.ry, 1e-6);
      if (dx * dx + dy * dy > 1) continue;
      const localIndex = (y - bounds.y0) * boundsWidth + (x - bounds.x0);
      inside[localIndex] = 1;
      const excluded = exclusionZones.some(
        (zone) =>
          nx >= zone.x && nx <= zone.x + zone.width && ny >= zone.y && ny <= zone.y + zone.height,
      );
      if (excluded) continue;
      scannedPixels += 1;
      const colour = rgbAt(data, width, channels, x, y);
      const features = pixelFeatures(colour.r, colour.g, colour.b);
      const detail = localDetailAt(luma, width, height, x, y);
      const isForeign = !isSkinLike(features, options) && !isHairLike(features, detail, options);
      if (nx < faceEllipse.centerX) {
        leftTotal += 1;
        if (isForeign) leftForeign += 1;
      } else {
        rightTotal += 1;
        if (isForeign) rightForeign += 1;
      }
      if (isForeign) foreign[localIndex] = 1;
    }
  }

  const components = analyseComponents(pixels, bounds, foreign);
  const largest = components[0];
  const coverage = scannedPixels > 0 && largest ? largest.area / scannedPixels : 0;
  const leftFraction = leftTotal > 0 ? leftForeign / leftTotal : 0;
  const rightFraction = rightTotal > 0 ? rightForeign / rightTotal : 0;
  const asymmetry = Math.abs(leftFraction - rightFraction);
  const boundarySharpness = largest?.boundarySharpness ?? 0;

  const sizeScore = clamp01(coverage / 0.15);
  const sharpScore = boundarySharpness;
  const asymmetryScore = clamp01(asymmetry / 0.4);
  const adequacy = clamp01(scannedPixels / 400);
  const confidence = clamp01(
    0.25 + 0.35 * sizeScore + 0.2 * sharpScore + 0.1 * asymmetryScore + 0.1 * adequacy,
  );

  if (scannedPixels < 400) {
    notes.push('Лицо занимает мало пикселей: крупные объекты могут быть не распознаны.');
  }

  return {
    coverage: roundTo(coverage, 4),
    largest,
    componentCount: components.length,
    asymmetry: roundTo(asymmetry, 3),
    boundarySharpness,
    scannedPixels,
    scanRect,
    faceEllipse,
    confidence: roundTo(confidence, 3),
    mode: 'heuristic',
    notes,
  };
};
